import { BaseIconProps } from 'components/icons/IconFactory'
import { InfoIcon, WarningIcon, XMarkIcon } from 'components/icons/Icons'
import { CheckCircleIcon } from 'lucide-react'
import { FC, useEffect, useState } from 'react'

type MessageType = 'success' | 'warning' | 'error' | 'info'
type MessageSize = 'sm' | 'md' | 'lg'

interface ActionButton {
  label?: string
  icon?: FC<BaseIconProps>
  onClick: () => void
  variant?: 'primary' | 'secondary'
}

interface GenericMessageProps {
  message: string
  className?: string
  type: MessageType
  size?: MessageSize
  dismissible?: boolean
  onDismiss?: () => void
  actionButton?: ActionButton
}

// Configuration des tailles
const SIZE_CONFIG = {
  sm: {
    containerClass: 'p-2',
    iconClass: 'size-4',
    textClass: 'text-xs',
    buttonClass: 'px-2 py-1 text-xs',
    dismissButtonClass: 'p-1',
    dismissIconClass: 'size-4'
  },
  md: {
    containerClass: 'p-4',
    iconClass: 'size-5',
    textClass: 'text-sm',
    buttonClass: 'px-3 py-1.5 text-xs',
    dismissButtonClass: 'p-1.5',
    dismissIconClass: 'size-5'
  },
  lg: {
    containerClass: 'p-5',
    iconClass: 'size-6',
    textClass: 'text-base',
    buttonClass: 'px-4 py-2 text-sm',
    dismissButtonClass: 'p-2',
    dismissIconClass: 'size-6'
  }
} as const

// Configuration des styles selon le type
const TYPE_CONFIG = {
  success: {
    bgClass: 'bg-green-50',
    textClass: 'text-green-800',
    iconClass: 'text-green-400',
    buttonClass: 'bg-green-50 text-green-500 hover:bg-green-100 focus:ring-green-600 focus:ring-offset-green-50',
    primaryButtonClass: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-600',
    secondaryButtonClass: 'bg-green-100 text-green-800 hover:bg-green-200 focus:ring-green-500',
    icon: CheckCircleIcon
  },
  warning: {
    bgClass: 'bg-yellow-50',
    textClass: 'text-yellow-800',
    iconClass: 'text-yellow-400',
    buttonClass: 'bg-yellow-50 text-yellow-500 hover:bg-yellow-100 focus:ring-yellow-600 focus:ring-offset-yellow-50',
    primaryButtonClass: 'bg-yellow-600 text-white hover:bg-yellow-700 focus:ring-yellow-600',
    secondaryButtonClass: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 focus:ring-yellow-500',
    icon: WarningIcon
  },
  error: {
    bgClass: 'bg-red-50',
    textClass: 'text-red-800',
    iconClass: 'text-red-400',
    buttonClass: 'bg-red-50 text-red-500 hover:bg-red-100 focus:ring-red-600 focus:ring-offset-red-50',
    primaryButtonClass: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-600',
    secondaryButtonClass: 'bg-red-100 text-red-800 hover:bg-red-200 focus:ring-red-500',
    icon: XMarkIcon
  },
  info: {
    bgClass: 'bg-primary/10',
    textClass: 'text-primary',
    iconClass: 'text-primary',
    buttonClass: 'bg-primary/5 text-primary hover:bg-primary/10 focus:ring-primary focus:ring-offset-primary/10',
    primaryButtonClass: 'bg-primary text-white hover:bg-primary/90 focus:ring-primary',
    secondaryButtonClass: 'bg-primary/10 text-primary hover:bg-primary/20 focus:ring-primary/50',
    icon: InfoIcon
  }
} as const

const getActionButtonClasses = (type: MessageType, variant: 'primary' | 'secondary' = 'secondary') => {
  const config = TYPE_CONFIG[type]
  return variant === 'primary' ? config.primaryButtonClass : config.secondaryButtonClass
}

export const GenericMessage: React.FC<GenericMessageProps> = ({
  message,
  className = '',
  type,
  size = 'md',
  dismissible = true,
  onDismiss,
  actionButton
}) => {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (message.trim()) {
      setIsVisible(true)
    }
  }, [message])

  const handleDismiss = () => {
    setIsVisible(false)
    onDismiss?.()
  }

  // Ne pas rendre le composant si le message est vide ou si le composant n'est pas visible
  if (!message.trim() || !isVisible) {
    return null
  }

  const sizeConfig = SIZE_CONFIG[size]
  const typeConfig = TYPE_CONFIG[type]
  const IconComponent = typeConfig.icon
  const ActionIcon = actionButton?.icon

  return (
    <div className={`rounded-md ${typeConfig.bgClass} ${sizeConfig.containerClass} ${className}`}>
      <div className="flex items-center">
        {/* Icône principale */}
        <div className="shrink-0">
          <IconComponent
            aria-hidden="true"
            className={`${sizeConfig.iconClass} ${typeConfig.iconClass}`}
          />
        </div>

        {/* Message */}
        <div className="ml-3 flex-1">
          <p className={`${sizeConfig.textClass} font-medium ${typeConfig.textClass}`}>
            {message}
          </p>
        </div>

        {/* Bouton d'action */}
        {actionButton && (
          <div className="ml-3">
            <button
              type="button"
              className={`inline-flex items-center rounded-md ${sizeConfig.buttonClass} font-medium ${getActionButtonClasses(type, actionButton.variant)} focus:outline-none focus:ring-2 focus:ring-offset-2`}
              onClick={actionButton.onClick}
            >
              {ActionIcon && <ActionIcon className="w-4 h-4 mr-1" />}
              {actionButton.label && actionButton.label}
            </button>
          </div>
        )}

        {/* Bouton de fermeture */}
        {dismissible && (
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                type="button"
                className={`inline-flex rounded-md ${sizeConfig.dismissButtonClass} ${typeConfig.buttonClass} focus:outline-none focus:ring-2 focus:ring-offset-2`}
                onClick={handleDismiss}
                aria-label="Fermer le message"
              >
                <span className="sr-only">Fermer</span>
                <XMarkIcon aria-hidden="true" className={sizeConfig.dismissIconClass} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
